import { useEffect, useState } from "react"
import {
  CodeSnippet01,
  CodeSnippet02,
  Copy01,
  LinkExternal01,
  Printer,
  QrCode02,
  Share01,
  Tablet01,
} from "@untitledui/icons"
import { toast } from "sonner"
import { makeQr } from "@/lib/intake/qr"
import { intakeUrls, publicOrigin, type ShopBranding } from "@/lib/intake/intake-config"
import { cx } from "@/lib/utils/cx"
import { downloadIntakePdf, type IntakePdfVariant } from "./counter-card-pdf"
import { copyText, cspNote, iframeSnippet, shopSlug, widgetSnippet } from "./snippets"

// "Share & embed" tab — every way the link leaves the building: copy/share,
// QR PNG, printable card/poster, website iframe, floating button, kiosk.
// Rule-divided sections in one container (brand rule). EVERY action here
// reports up through onShared() so the sidebar's "never shared" glyph clears
// the first time the shop actually distributes anything.

interface DistributePanelProps {
  shopId: string
  shopName: string
  // Team phoneNumber — printed on the counter card / poster.
  shopPhone: string | null
  branding: ShopBranding
  onShared: () => void
}

const QR_SIZES = [512, 1024, 2048] as const

export function DistributePanel({
  shopId,
  shopName,
  shopPhone,
  branding,
  onShared,
}: DistributePanelProps) {
  const origin = publicOrigin()
  const urls = intakeUrls(origin, shopId)
  const slug = shopSlug(shopName)

  const copy = async (text: string, what: string) => {
    onShared()
    const ok = await copyText(text)
    if (ok) toast.success(`${what} copied`)
    else toast.error("Couldn't copy — select and copy it manually")
  }

  // navigator.share where it exists (phones, some desktops), clipboard
  // everywhere else — same fallback as the advisor referral link.
  const share = async () => {
    onShared()
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Get an estimate from ${shopName}`,
          text: "Send a few photos of the damage and we'll get your estimate started:",
          url: urls.link,
        })
      } else {
        const ok = await copyText(urls.link)
        if (ok) toast.success("Link copied — paste it in a text or email")
        else toast.error("Couldn't copy")
      }
    } catch {
      /* user closed the share sheet — nothing to do */
    }
  }

  return (
    <div className="divide-y divide-zinc-100 rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
      {/* (a) Link */}
      <Section
        icon={Share01}
        title="Your link"
        sub="Text it, put it in your email signature, add it to Google Business."
      >
        <div className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50/60 px-3.5 py-2.5">
          <span className="min-w-0 flex-1 truncate font-mono text-[13px] text-zinc-700">
            {urls.link}
          </span>
          <ActionButton icon={Copy01} label="Copy" onClick={() => copy(urls.link, "Link")} />
          <a
            href={urls.link}
            target="_blank"
            rel="noreferrer"
            onClick={onShared}
            className={actionButtonClass(false)}
          >
            <LinkExternal01 className="size-3.5" aria-hidden />
            Open
          </a>
          <ActionButton icon={Share01} label="Share" primary onClick={share} />
        </div>
      </Section>

      {/* (b) QR */}
      <QrSection urls={{ qr: urls.qr }} slug={slug} onShared={onShared} />

      {/* (c) Printable */}
      <PrintSection
        shopName={shopName}
        shopPhone={shopPhone}
        branding={branding}
        urls={{ link: urls.link, qr: urls.qr }}
        slug={slug}
        onShared={onShared}
      />

      {/* (d) Iframe snippet */}
      <Section
        icon={CodeSnippet02}
        title="Put the form on your website"
        sub="Paste this where the form should appear — it sizes itself."
      >
        <Snippet
          text={iframeSnippet(origin, shopId, shopName)}
          onCopy={() => copy(iframeSnippet(origin, shopId, shopName), "Embed code")}
        />
        <p className="mt-2 text-[11.5px] leading-snug text-zinc-400">{cspNote(origin)}</p>
      </Section>

      {/* (e) Floating button */}
      <Section
        icon={CodeSnippet01}
        title="Floating button"
        sub="One script tag — adds a “Get an estimate” button to every page."
      >
        <Snippet
          text={widgetSnippet(origin, shopId, branding.primaryColor || "#059669")}
          onCopy={() =>
            copy(widgetSnippet(origin, shopId, branding.primaryColor || "#059669"), "Button code")
          }
        />
      </Section>

      {/* (f) Kiosk */}
      <Section
        icon={Tablet01}
        title="Lobby kiosk"
        sub="Open on the tablet, tap once, it runs by itself. iPad: Add to Home Screen for true full-screen."
      >
        <a
          href={urls.kiosk}
          target="_blank"
          rel="noreferrer"
          onClick={onShared}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-[12.5px] font-semibold text-white hover:bg-black"
        >
          Open kiosk
          <LinkExternal01 className="size-3.5" aria-hidden />
        </a>
      </Section>
    </div>
  )
}

function QrSection({
  urls,
  slug,
  onShared,
}: {
  urls: { qr: string }
  slug: string
  onShared: () => void
}) {
  const [preview, setPreview] = useState<string | null>(null)
  const [size, setSize] = useState<number>(1024)
  const [busy, setBusy] = useState(false)

  // 320px source drawn at 160px — crisp on retina without the full-size cost.
  useEffect(() => {
    let alive = true
    makeQr(urls.qr, { size: 320 })
      .then((d) => {
        if (alive) setPreview(d)
      })
      .catch(() => {
        if (alive) setPreview(null)
      })
    return () => {
      alive = false
    }
  }, [urls.qr])

  const download = async () => {
    if (busy) return
    setBusy(true)
    onShared()
    try {
      const dataUrl = await makeQr(urls.qr, { size })
      const a = document.createElement("a")
      a.href = dataUrl
      a.download = `${slug}-intake-qr-${size}.png`
      a.click()
    } catch {
      toast.error("Couldn't generate the QR code")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Section
      icon={QrCode02}
      title="QR code"
      sub="Scans straight to your photo flow — counters, mirror hangers, invoices."
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="grid size-[160px] shrink-0 place-items-center rounded-xl border border-zinc-200 bg-white p-2">
          {preview ? (
            <img src={preview} alt={`QR code for ${urls.qr}`} className="size-full" />
          ) : (
            <span className="text-[11px] text-zinc-400">Generating…</span>
          )}
        </div>
        <div className="grid gap-2.5">
          <div className="flex items-center gap-1 rounded-lg bg-zinc-100 p-1" role="radiogroup" aria-label="PNG size">
            {QR_SIZES.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={size === s}
                onClick={() => setSize(s)}
                className={cx(
                  "h-8 rounded-md px-3 text-[12px] font-semibold tabular-nums transition-colors",
                  size === s ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800",
                )}
              >
                {s}px
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={download}
            className="inline-flex h-9 w-fit items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-[12.5px] font-semibold text-white hover:bg-black disabled:opacity-50"
          >
            {busy ? "Generating…" : "Download PNG"}
          </button>
          <p className="text-[11.5px] text-zinc-400">
            1024px prints clean up to about 8 inches wide.
          </p>
        </div>
      </div>
    </Section>
  )
}

function PrintSection({
  shopName,
  shopPhone,
  branding,
  urls,
  slug,
  onShared,
}: {
  shopName: string
  shopPhone: string | null
  branding: ShopBranding
  urls: { link: string; qr: string }
  slug: string
  onShared: () => void
}) {
  const [printing, setPrinting] = useState<IntakePdfVariant | null>(null)

  const print = async (variant: IntakePdfVariant) => {
    if (printing) return
    setPrinting(variant)
    onShared()
    try {
      await downloadIntakePdf({
        variant,
        shopName,
        shortUrl: urls.link.replace(/^https?:\/\//, ""),
        qrUrl: urls.qr,
        phone: shopPhone,
        hours: branding.hours,
        primaryColor: branding.primaryColor || "#059669",
        logoUrl: branding.logoUrl,
        logoInitials: branding.logoInitials || shopName.slice(0, 2),
        slug,
      })
    } catch {
      toast.error("Couldn't build the PDF")
    } finally {
      setPrinting(null)
    }
  }

  return (
    <Section
      icon={Printer}
      title="Printable counter card & poster"
      sub="Ready-to-print PDFs with your QR code — by the register and on the wall."
    >
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={printing !== null}
          onClick={() => print("card")}
          className={secondaryButtonClass}
        >
          {printing === "card" ? "Building…" : "Counter card · 5×7 in"}
        </button>
        <button
          type="button"
          disabled={printing !== null}
          onClick={() => print("poster")}
          className={secondaryButtonClass}
        >
          {printing === "poster" ? "Building…" : "Poster · Letter"}
        </button>
      </div>
    </Section>
  )
}

function Section({
  icon: Icon,
  title,
  sub,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  sub: string
  children: React.ReactNode
}) {
  return (
    <section className="p-5 md:p-6">
      <div className="flex items-start gap-2.5">
        <Icon className="mt-0.5 size-4 shrink-0 text-zinc-400" aria-hidden />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-zinc-800">{title}</p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-zinc-500">{sub}</p>
        </div>
      </div>
      <div className="mt-3 pl-[26px]">{children}</div>
    </section>
  )
}

function Snippet({ text, onCopy }: { text: string; onCopy: () => void }) {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-xl bg-zinc-900 p-3.5 pr-20 font-mono text-[11px] leading-relaxed text-zinc-200">
        {text}
      </pre>
      <button
        type="button"
        onClick={onCopy}
        className="absolute right-2.5 top-2.5 inline-flex h-7 items-center gap-1 rounded-md bg-white/10 px-2.5 text-[11.5px] font-semibold text-zinc-200 transition-colors hover:bg-white/20"
      >
        <Copy01 className="size-3" aria-hidden />
        Copy
      </button>
    </div>
  )
}

const secondaryButtonClass =
  "inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3.5 text-[12.5px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"

function actionButtonClass(primary: boolean) {
  return cx(
    "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-semibold transition-colors",
    primary
      ? "bg-zinc-900 text-white hover:bg-black"
      : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50",
  )
}

function ActionButton({
  icon: Icon,
  label,
  primary = false,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  primary?: boolean
  onClick: () => void
}) {
  return (
    <button type="button" onClick={onClick} className={actionButtonClass(primary)}>
      <Icon className="size-3.5" aria-hidden />
      {label}
    </button>
  )
}
